import { useEffect, useState, type ReactNode } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  InputAdornment,
  Link,
  MenuItem,
  Skeleton,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import CelebrationRounded from '@mui/icons-material/CelebrationRounded';
import OpenInNewRounded from '@mui/icons-material/OpenInNewRounded';
import { useNavigate } from 'react-router';
import { ApiError } from '@/api';
import { useAuth } from '@/auth';
import { useAsync } from '@/hooks/useAsync';
import { useSession } from '@/platform/session';
import { getCatalog, signUpInstitution } from './api';
import { PageShell as Shell } from './PageShell';
import {
  CATEGORIES,
  clearPlanIntent,
  formatDate,
  formatMoney,
  ORGANIZATIONS,
  readPlanIntent,
  STATES,
} from './labels';
import { DEFAULT_CHOICE, normalizeChoice } from './choice';
import { PlanPicker, QuoteSummary } from './PlanPicker';
import type { InstitutionSignupInput, PlanChoice, SignupResult } from './types';

const STEPS = ['Instituição', 'Endereço', 'Responsável e e-mails', 'Plano'];

type Form = Omit<InstitutionSignupInput, keyof PlanChoice | 'acceptTerms'>;
type Field = keyof Form;

const EMPTY: Form = {
  name: '',
  legalName: '',
  cnpj: '',
  emecCode: '',
  organization: '',
  category: '',
  website: '',
  phone: '',
  postalCode: '',
  address: '',
  addressNumber: '',
  complement: '',
  district: '',
  city: '',
  state: '',
  contactName: '',
  contactRole: '',
  contactPhone: '',
  studentDomain: '',
  teacherDomain: '',
};

// ── Masks and checks (the API checks again) ─────────────────────────────────

const onlyDigits = (v: string) => v.replace(/\D/g, '');

/** 12.345.678/0001-90 (letters allowed in the first 12 characters since July 2026). */
function maskCnpj(value: string) {
  const raw = value
    .toUpperCase()
    .replace(/[^0-9A-Z]/g, '')
    .slice(0, 14);
  return raw
    .replace(/^(.{2})(.)/, '$1.$2')
    .replace(/^(.{2})\.(.{3})(.)/, '$1.$2.$3')
    .replace(/^(.{2})\.(.{3})\.(.{3})(.)/, '$1.$2.$3/$4')
    .replace(/^(.{2})\.(.{3})\.(.{3})\/(.{4})(.)/, '$1.$2.$3/$4-$5');
}

function validCnpj(value: string) {
  const cnpj = value.toUpperCase().replace(/[.\-/\s]/g, '');
  if (!/^[0-9A-Z]{12}[0-9]{2}$/.test(cnpj) || new Set(cnpj).size === 1) return false;
  const values = [...cnpj].map((c) => c.charCodeAt(0) - 48);
  const check = (n: number) => {
    const weights = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2].slice(-n);
    const rest = values.slice(0, n).reduce((sum, v, i) => sum + v * weights[i], 0) % 11;
    return rest < 2 ? 0 : 11 - rest;
  };
  return check(12) === values[12] && check(13) === values[13];
}

function maskPhone(value: string) {
  const d = onlyDigits(value).slice(0, 11);
  if (d.length <= 2) return d;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

const maskCep = (value: string) =>
  onlyDigits(value)
    .slice(0, 8)
    .replace(/^(\d{5})(\d)/, '$1-$2');

const DOMAIN = /^(?!-)[a-z0-9-]+(\.[a-z0-9-]+)+$/;
const WEBMAIL = [
  'gmail.com',
  'hotmail.com',
  'outlook.com',
  'outlook.com.br',
  'yahoo.com',
  'yahoo.com.br',
  'icloud.com',
  'live.com',
  'bol.com.br',
  'uol.com.br',
];

function domainError(value: string) {
  const domain = value.trim().toLowerCase().replace(/^@/, '');
  if (!domain) return 'Informe o domínio.';
  if (!DOMAIN.test(domain)) return 'Domínio inválido (ex.: aluno.suaies.edu.br).';
  if (WEBMAIL.includes(domain)) return 'Use o domínio da instituição, não um e-mail pessoal.';
  return undefined;
}

function errorsOf(form: Form, step: number): Partial<Record<Field, string>> {
  const e: Partial<Record<Field, string>> = {};
  const need = (field: Field, message: string, min = 2) => {
    if (form[field].trim().length < min) e[field] = message;
  };
  if (step === 0) {
    need('name', 'Como a instituição é conhecida (ex.: UniAlfa).');
    need('legalName', 'A razão social do CNPJ.', 3);
    if (!validCnpj(form.cnpj)) e.cnpj = 'CNPJ inválido. Confira os números.';
    if (!/^\d{1,10}$/.test(onlyDigits(form.emecCode)) || !form.emecCode.trim())
      e.emecCode = 'Só números. Consulte em emec.mec.gov.br.';
    if (!form.organization) e.organization = 'Escolha a organização acadêmica.';
    if (!form.category) e.category = 'Escolha a categoria administrativa.';
    if (onlyDigits(form.phone).length < 10) e.phone = 'DDD + número.';
  } else if (step === 1) {
    if (onlyDigits(form.postalCode).length !== 8) e.postalCode = 'CEP com 8 números.';
    need('address', 'Rua, avenida...', 3);
    need('addressNumber', 'Número (ou s/n).', 1);
    need('district', 'Bairro.');
    need('city', 'Cidade.');
    if (!STATES.includes(form.state)) e.state = 'UF.';
  } else if (step === 2) {
    if (form.contactName.trim().split(/\s+/).length < 2) e.contactName = 'Nome e sobrenome.';
    need('contactRole', 'Ex.: Coordenador(a) de curso, Pró-reitor(a).');
    if (onlyDigits(form.contactPhone).length < 10) e.contactPhone = 'DDD + número.';
    const s = domainError(form.studentDomain);
    const t = domainError(form.teacherDomain);
    if (s) e.studentDomain = s;
    if (t) e.teacherDomain = t;
  }
  return e;
}

/** Fills the address from the CEP (ViaCEP, a free public service). */
async function lookupCep(cep: string) {
  const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
  if (!response.ok) return null;
  const data = (await response.json()) as {
    erro?: boolean;
    logradouro?: string;
    bairro?: string;
    localidade?: string;
    uf?: string;
  };
  return data.erro ? null : data;
}

/**
 * An IES signs up by itself: who it is (CNPJ, e-MEC), where, who answers for it, the e-mail
 * domains of its students and teachers, and the plan. The person signed in becomes its
 * coordinator; the first invoice falls due at the end of the free trial.
 */
export function InstitutionSignupPage({ onBack }: { onBack?: () => void }) {
  const { signOut } = useAuth();
  const { user, refresh } = useSession();
  const navigate = useNavigate();
  const catalog = useAsync(getCatalog);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState<Form>({ ...EMPTY, contactName: user.name ?? '' });
  const [picked, setChoice] = useState<PlanChoice>(() => readPlanIntent() ?? DEFAULT_CHOICE);
  const [touched, setTouched] = useState(false);
  const [accept, setAccept] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<SignupResult | null>(null);
  // The last CEP looked up and whether it was found.
  const [cepLookup, setCepLookup] = useState<{ cep: string; found: boolean } | null>(null);

  const errors = touched ? errorsOf(form, step) : {};
  const set = (field: Field, value: string) => setForm((f) => ({ ...f, [field]: value }));
  const field = (name: Field) => ({
    value: form[name],
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => set(name, e.target.value),
    error: Boolean(errors[name]),
    helperText: errors[name],
    fullWidth: true,
  });

  // A plan picked on the plans page (or the default) must fit the catalog.
  const data = catalog.data;
  const choice = data ? normalizeChoice(data, picked) : picked;

  // CEP → address.
  const cep = onlyDigits(form.postalCode);
  const cepState =
    cep.length !== 8
      ? 'idle'
      : cepLookup?.cep !== cep
        ? 'loading'
        : cepLookup.found
          ? 'idle'
          : 'missing';
  useEffect(() => {
    if (cep.length !== 8) return;
    let alive = true;
    lookupCep(cep)
      .then((found) => {
        if (!alive) return;
        setCepLookup({ cep, found: Boolean(found) });
        if (found)
          setForm((f) => ({
            ...f,
            address: found.logradouro || f.address,
            district: found.bairro || f.district,
            city: found.localidade || f.city,
            state: found.uf || f.state,
          }));
      })
      .catch(() => alive && setCepLookup({ cep, found: true }));
    return () => {
      alive = false;
    };
  }, [cep]);

  const next = () => {
    setTouched(true);
    if (Object.keys(errorsOf(form, step)).length) return;
    setTouched(false);
    setStep((s) => s + 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const submit = async () => {
    setError(null);
    if (!accept) {
      setError('Para continuar, aceite os termos de uso e a política de privacidade.');
      return;
    }
    setPending(true);
    try {
      const done = await signUpInstitution({
        ...form,
        cnpj: form.cnpj.replace(/[.\-/\s]/g, ''),
        studentDomain: form.studentDomain.trim().toLowerCase().replace(/^@/, ''),
        teacherDomain: form.teacherDomain.trim().toLowerCase().replace(/^@/, ''),
        ...choice,
        acceptTerms: accept,
      });
      clearPlanIntent();
      setResult(done);
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : 'Não foi possível cadastrar. Tente de novo.',
      );
      // Errors about the institution's data: go back to where they are.
      if (err instanceof ApiError) {
        const code = (err.data as { code?: string } | undefined)?.code ?? '';
        if (['invalid-cnpj', 'invalid-emec', 'cnpj-in-use', 'name-in-use'].includes(code))
          setStep(0);
        else if (['invalid-state', 'invalid-postal-code'].includes(code)) setStep(1);
        else if (['invalid-domain', 'invalid-name'].includes(code)) setStep(2);
      }
    } finally {
      setPending(false);
    }
  };

  const enter = () => {
    void navigate('/assinatura');
    void refresh();
  };

  return (
    <Shell onSignOut={() => void signOut()}>
      {result ? (
        <Done result={result} onEnter={enter} />
      ) : (
        <>
          <Stack spacing={0.5} sx={{ mb: 3 }}>
            <Typography variant="h4" component="h1">
              Cadastre sua instituição
            </Typography>
            <Typography color="text.secondary">
              Precisamos de alguns dados que identificam a IES no MEC. Leva uns 3 minutos.
            </Typography>
          </Stack>
          {!user.emailVerified && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              Entre com a conta Google de quem responde pela instituição para concluir o cadastro.
            </Alert>
          )}
          <Stepper activeStep={step} alternativeLabel sx={{ mb: 4 }}>
            {STEPS.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {step === 0 && (
            <Grid2>
              <TextField
                label="Nome da instituição"
                placeholder="Ex.: UniAlfa"
                autoFocus
                {...field('name')}
              />
              <TextField
                label="Razão social"
                placeholder="Como consta no CNPJ"
                {...field('legalName')}
              />
              <TextField
                label="CNPJ"
                {...field('cnpj')}
                onChange={(e) => set('cnpj', maskCnpj(e.target.value))}
                slotProps={{ htmlInput: { inputMode: 'text', autoCapitalize: 'characters' } }}
              />
              <TextField
                label="Código da IES no e-MEC"
                {...field('emecCode')}
                onChange={(e) => set('emecCode', onlyDigits(e.target.value).slice(0, 10))}
                helperText={
                  errors.emecCode ?? (
                    <>
                      Encontre em{' '}
                      <Link href="https://emec.mec.gov.br" target="_blank" rel="noopener">
                        emec.mec.gov.br
                      </Link>
                    </>
                  )
                }
                slotProps={{ htmlInput: { inputMode: 'numeric' } }}
              />
              <TextField select label="Organização acadêmica" {...field('organization')}>
                {Object.entries(ORGANIZATIONS).map(([value, label]) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Categoria administrativa"
                {...field('category')}
                helperText={errors.category}
              >
                {Object.entries(CATEGORIES).map(([value, label]) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Site"
                placeholder="www.suaies.edu.br"
                {...field('website')}
                helperText="Opcional."
              />
              <TextField
                label="Telefone da instituição"
                {...field('phone')}
                onChange={(e) => set('phone', maskPhone(e.target.value))}
                slotProps={{ htmlInput: { inputMode: 'tel' } }}
              />
            </Grid2>
          )}

          {step === 1 && (
            <Grid2>
              <TextField
                label="CEP"
                autoFocus
                {...field('postalCode')}
                onChange={(e) => set('postalCode', maskCep(e.target.value))}
                helperText={
                  errors.postalCode ??
                  (cepState === 'missing'
                    ? 'CEP não encontrado: preencha o endereço.'
                    : 'Preenchemos o endereço para você.')
                }
                slotProps={{
                  htmlInput: { inputMode: 'numeric' },
                  input: {
                    endAdornment: cepState === 'loading' && (
                      <InputAdornment position="end">
                        <CircularProgress size={18} />
                      </InputAdornment>
                    ),
                  },
                }}
              />
              <TextField label="Logradouro" {...field('address')} />
              <TextField label="Número" {...field('addressNumber')} />
              <TextField label="Complemento" {...field('complement')} helperText="Opcional." />
              <TextField label="Bairro" {...field('district')} />
              <Stack direction="row" spacing={2}>
                <TextField label="Cidade" {...field('city')} />
                <TextField select label="UF" {...field('state')} sx={{ width: 110, flexShrink: 0 }}>
                  {STATES.map((uf) => (
                    <MenuItem key={uf} value={uf}>
                      {uf}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>
            </Grid2>
          )}

          {step === 2 && (
            <Stack spacing={4}>
              <Box>
                <SectionTitle
                  title="Quem responde pela instituição"
                  text="Esta pessoa vira a coordenação da instituição no Lousa e recebe as faturas."
                />
                <Grid2>
                  <TextField label="Nome completo" autoFocus {...field('contactName')} />
                  <TextField
                    label="Cargo"
                    placeholder="Coordenador(a) de curso"
                    {...field('contactRole')}
                  />
                  <TextField
                    label="E-mail"
                    value={user.email}
                    disabled
                    fullWidth
                    helperText="O da sua conta Google."
                  />
                  <TextField
                    label="Celular"
                    {...field('contactPhone')}
                    onChange={(e) => set('contactPhone', maskPhone(e.target.value))}
                    slotProps={{ htmlInput: { inputMode: 'tel' } }}
                  />
                </Grid2>
              </Box>
              <Box>
                <SectionTitle
                  title="E-mails institucionais"
                  text="Estudantes e professores provam que são da instituição confirmando um e-mail nestes domínios."
                />
                <Grid2>
                  <TextField
                    label="Domínio dos estudantes"
                    placeholder="aluno.suaies.edu.br"
                    {...field('studentDomain')}
                    helperText={
                      errors.studentDomain ??
                      `Ex.: maria@${form.studentDomain.trim() || 'aluno.suaies.edu.br'}`
                    }
                    slotProps={{
                      input: {
                        startAdornment: <InputAdornment position="start">@</InputAdornment>,
                      },
                    }}
                  />
                  <TextField
                    label="Domínio dos professores"
                    placeholder="suaies.edu.br"
                    {...field('teacherDomain')}
                    helperText={
                      errors.teacherDomain ??
                      'Pode ser o mesmo dos estudantes. A coordenação aprova cada professor(a).'
                    }
                    slotProps={{
                      input: {
                        startAdornment: <InputAdornment position="start">@</InputAdornment>,
                      },
                    }}
                  />
                </Grid2>
              </Box>
            </Stack>
          )}

          {step === 3 &&
            (!data ? (
              <Skeleton variant="rounded" height={360} />
            ) : (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) 340px' },
                  gap: 3,
                  alignItems: 'start',
                }}
              >
                <PlanPicker catalog={data} value={choice} onChange={setChoice} dense />
                <Box sx={{ position: { md: 'sticky' }, top: { md: 24 } }}>
                  <QuoteSummary
                    choice={choice}
                    trialDays={data.trialDays}
                    footer={
                      <Stack spacing={1.5} sx={{ mt: 2 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={accept}
                              onChange={(e) => setAccept(e.target.checked)}
                            />
                          }
                          label={
                            <Typography variant="body2">
                              Li e aceito os termos de uso e a{' '}
                              <Link href="/privacidade.html" target="_blank" rel="noopener">
                                política de privacidade
                              </Link>
                              , e confirmo que represento esta instituição.
                            </Typography>
                          }
                        />
                        <Button
                          variant="contained"
                          size="large"
                          onClick={() => void submit()}
                          disabled={pending || !user.emailVerified}
                        >
                          {pending ? (
                            <CircularProgress size={22} color="inherit" />
                          ) : data.trialDays ? (
                            `Começar ${data.trialDays} dias grátis`
                          ) : (
                            'Cadastrar e ir para o pagamento'
                          )}
                        </Button>
                      </Stack>
                    }
                  />
                </Box>
              </Box>
            ))}

          <Stack direction="row" spacing={1.5} sx={{ mt: 4, justifyContent: 'space-between' }}>
            {step > 0 ? (
              <Button
                startIcon={<ArrowBackRounded />}
                color="inherit"
                onClick={() => setStep((s) => s - 1)}
              >
                Voltar
              </Button>
            ) : onBack ? (
              <Button startIcon={<ArrowBackRounded />} color="inherit" onClick={onBack}>
                Sou estudante ou professor(a)
              </Button>
            ) : (
              <span />
            )}
            {step < STEPS.length - 1 && (
              <Button variant="contained" endIcon={<ArrowForwardRounded />} onClick={next}>
                Continuar
              </Button>
            )}
          </Stack>
        </>
      )}
    </Shell>
  );
}

function Done({ result, onEnter }: { result: SignupResult; onEnter: () => void }) {
  const { institution, checkoutUrl } = result;
  const sub = institution.subscription;
  const trial = institution.status === 'trial';
  return (
    <Stack spacing={3} sx={{ alignItems: 'center', textAlign: 'center', py: 2 }}>
      <Box
        sx={(t) => ({
          width: 72,
          height: 72,
          borderRadius: 5,
          display: 'grid',
          placeItems: 'center',
          bgcolor: t.vars.palette.lime.container,
          color: t.vars.palette.lime.onContainer,
        })}
      >
        <CelebrationRounded sx={{ fontSize: 36 }} />
      </Box>
      <Box>
        <Typography variant="h4" component="h1">
          {institution.name} está no Lousa!
        </Typography>
        <Typography color="text.secondary" sx={{ mt: 1, maxWidth: 520 }}>
          Você é a coordenação da instituição. Convide professores e estudantes: eles entram com a
          conta Google e o e-mail institucional.
        </Typography>
      </Box>
      <Alert
        severity={trial ? 'success' : 'info'}
        sx={{ textAlign: 'left', width: '100%', maxWidth: 560 }}
      >
        <AlertTitle>
          {trial
            ? `Período de teste até ${formatDate(sub?.trialEndsAt)}`
            : 'Falta pagar a primeira fatura'}
        </AlertTitle>
        Plano {sub?.planName}, {sub?.seats} estudantes: {formatMoney(sub?.amountCents)}{' '}
        {sub?.cycle === 'once' ? '(pagamento único)' : 'por ciclo'}.{' '}
        {trial
          ? 'A primeira fatura vence no fim do teste. Pode pagar antes, se preferir.'
          : 'O acesso começa assim que o pagamento for confirmado.'}
      </Alert>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
        {checkoutUrl && (
          <Button
            variant={trial ? 'outlined' : 'contained'}
            href={checkoutUrl}
            target="_blank"
            rel="noopener"
            endIcon={<OpenInNewRounded />}
          >
            Ver a fatura (PIX, boleto ou cartão)
          </Button>
        )}
        <Button variant={trial ? 'contained' : 'outlined'} onClick={onEnter}>
          Ir para o Lousa
        </Button>
      </Stack>
    </Stack>
  );
}

function SectionTitle({ title, text }: { title: string; text: string }) {
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 600 }}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {text}
      </Typography>
    </Box>
  );
}

function Grid2({ children }: { children: ReactNode }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2.5 }}>
      {children}
    </Box>
  );
}
